import { useEffect, useRef } from "react";

/**
 * Atmospheric "embers rising" particle field for the login page.
 *
 * Particles spawn near the bottom and drift upward (a bit like champagne
 * bubbles, a bit like sparks lifting off a fire). Each particle has a slight
 * horizontal sway and fades out as it nears the top, so the effect feels
 * alive without becoming visually busy. Tinted in the brand's warm red
 * palette so it stacks under the radial-red gradient on the login page.
 *
 * No interactivity — the previous mouse-pull was distracting on a login
 * screen where the user just wants to type and go.
 */
export function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let dpr = Math.max(1, window.devicePixelRatio || 1);
    let width = window.innerWidth;
    let height = window.innerHeight;

    // Tunables — picked by eye for a "sophisticated, alive" feel.
    let PARTICLE_COUNT = Math.floor((width * height) / 14000);

    interface P {
      x: number;
      y: number;
      vy: number; // upward velocity (negative pixel/ms)
      vx: number; // gentle drift
      phase: number; // for horizontal sway
      sway: number; // amplitude of sway
      r: number; // radius
      hueShift: number; // 0..1 — picks among red palette stops
      lifeY: number; // y where this particle was born (used for fade)
    }
    const particles: P[] = [];

    const resize = () => {
      dpr = Math.max(1, window.devicePixelRatio || 1);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      PARTICLE_COUNT = Math.floor((width * height) / 14000);
      // Top up if we resized larger; trim if smaller.
      while (particles.length < PARTICLE_COUNT) particles.push(spawn(true));
      while (particles.length > PARTICLE_COUNT) particles.pop();
    };

    function spawn(initial: boolean): P {
      // On initial population, scatter across the full height so the screen
      // isn't visually empty for the first few seconds. After that, particles
      // spawn just below the bottom edge so they drift up into view.
      const y = initial
        ? Math.random() * height
        : height + Math.random() * 60;
      const r = 0.8 + Math.random() * 2.2;
      return {
        x: Math.random() * width,
        y,
        // Bigger particles rise slightly slower (heavier embers feel),
        // but everything is slow — full traversal ~25–60 seconds.
        vy: -(0.012 + Math.random() * 0.025) / Math.max(0.6, r * 0.6),
        vx: (Math.random() - 0.5) * 0.008,
        phase: Math.random() * Math.PI * 2,
        sway: 0.2 + Math.random() * 0.6,
        r,
        hueShift: Math.random(),
        lifeY: y,
      };
    }

    resize();
    window.addEventListener("resize", resize);

    // Police-siren palette: half-red embers, half-blue embers, mixed in
    // the air. The hueShift property on each particle picks an index here
    // — distribution is roughly 50/50 red/blue with a few brighter
    // highlights. Format strings include a trailing `, ALPHA)` filled
    // per-frame.
    const palette = [
      "228, 0, 43",    // siren red (deep)
      "239, 68, 68",   // brighter red highlight
      "248, 113, 113", // soft red glow
      "0, 70, 255",    // siren blue (electric)
      "59, 130, 246",  // brighter blue highlight
      "147, 197, 253", // soft blue glow
    ];

    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = Math.min(34, now - last);
      last = now;
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        p.phase += dt * 0.0009;
        p.x += (p.vx + Math.sin(p.phase) * 0.012 * p.sway) * dt;
        p.y += p.vy * dt;

        // Wrap horizontally (gentle nudge — looks more natural than hard wrap).
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;

        // Respawn when above the top, or with a tiny chance to keep things
        // varied (so we're not always spawning bursts).
        if (p.y < -20) {
          particles[i] = spawn(false);
          continue;
        }

        // Alpha: full strength near where it was born, fading as it climbs
        // toward the top. Capped, multiplied by a subtle base so faint at edges.
        const climbed = (p.lifeY - p.y) / Math.max(p.lifeY, 1);
        const a = Math.max(0, 0.55 - climbed * 0.55) * (0.45 + p.r * 0.18);

        const colorIdx = Math.floor(p.hueShift * palette.length);
        const rgb = palette[Math.min(colorIdx, palette.length - 1)]!;

        // Soft glow via two passes — a faint outer halo + bright core.
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 3.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${a * 0.10})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb}, ${a})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
    />
  );
}
